import Spinner from '@/common/components/Spinner'
import { Button } from '@/common/ui/button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { ScrollArea, ScrollBar } from '@/common/ui/scroll-area'
import {
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import {
	ColumnDef,
	flexRender,
	getCoreRowModel,
	getPaginationRowModel,
	useReactTable,
} from '@tanstack/react-table'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import {
	DoubleArrowLeftIcon,
	DoubleArrowRightIcon,
} from '@radix-ui/react-icons'

interface DataTableProps<TData, TValue> {
	columns: ColumnDef<TData, TValue>[]
	data: TData[]
	isLoading?: boolean
	footer?: React.ReactNode
	pagination?: boolean
}

const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50]

export function DataTable<TData, TValue>({
	columns,
	data,
	isLoading,
	footer,
	pagination = false,
}: DataTableProps<TData, TValue>) {
	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
		manualPagination: !pagination,
		manualFiltering: true,
	})

	if (isLoading) {
		return <Spinner />
	}

	return (
		<div className="space-y-4">
			<ScrollArea className="rounded-md border">
				<Table className="relative">
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<TableHead key={header.id}>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
									</TableHead>
								))}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((row) => (
								<TableRow
									key={row.id}
									data-state={row.getIsSelected() && 'selected'}
								>
									{row.getVisibleCells().map((cell) => (
										<TableCell key={cell.id}>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</TableCell>
									))}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell
									colSpan={columns.length}
									className="h-24 text-center"
								>
									ไม่มีข้อมูล.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
					{footer && (
						<TableFooter>
							<TableRow>{footer}</TableRow>
						</TableFooter>
					)}
				</Table>
				<ScrollBar orientation="horizontal" />
			</ScrollArea>

			{pagination && (
				<div className="flex flex-col items-center justify-end gap-2 py-4 sm:flex-row">
					<div className="flex w-full items-center justify-between">
						<div className="flex-1 text-sm text-muted-foreground">
							{data.length > 0 ? (
								<>
									แสดง{' '}
									{table.getState().pagination.pageIndex *
										table.getState().pagination.pageSize +
										1}{' '}
									ถึง{' '}
									{Math.min(
										(table.getState().pagination.pageIndex + 1) *
											table.getState().pagination.pageSize,
										data.length,
									)}{' '}
									จาก {data.length} รายการ
								</>
							) : (
								'ไม่มีรายการ'
							)}
						</div>
						<div className="flex items-center gap-4 sm:gap-6 lg:gap-8">
							<div className="flex items-center space-x-2">
								<p className="whitespace-nowrap text-sm font-medium">
									แถวต่อหน้า
								</p>
								<Select
									value={`${table.getState().pagination.pageSize}`}
									onValueChange={(value) => {
										table.setPageSize(Number(value))
									}}
								>
									<SelectTrigger className="h-8 w-[70px]">
										<SelectValue
											placeholder={table.getState().pagination.pageSize}
										/>
									</SelectTrigger>
									<SelectContent side="top">
										{PAGE_SIZE_OPTIONS.map((pageSize) => (
											<SelectItem key={pageSize} value={`${pageSize}`}>
												{pageSize}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
					</div>
					<div className="flex w-full items-center justify-between gap-2 sm:justify-end">
						<div className="flex w-[150px] items-center justify-center text-sm font-medium">
							{data.length > 0 ? (
								<>
									หน้า {table.getState().pagination.pageIndex + 1} จาก{' '}
									{table.getPageCount()}
								</>
							) : (
								'ไม่มีหน้า'
							)}
						</div>
						<div className="flex items-center space-x-2">
							<Button
								aria-label="ไปหน้าแรก"
								variant="outline"
								className="hidden h-8 w-8 p-0 lg:flex"
								onClick={() => table.setPageIndex(0)}
								disabled={!table.getCanPreviousPage()}
							>
								<DoubleArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
							</Button>
							<Button
								aria-label="ไปหน้าก่อนหน้า"
								variant="outline"
								className="h-8 w-8 p-0"
								onClick={() => table.previousPage()}
								disabled={!table.getCanPreviousPage()}
							>
								<ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
							</Button>
							<Button
								aria-label="ไปหน้าถัดไป"
								variant="outline"
								className="h-8 w-8 p-0"
								onClick={() => table.nextPage()}
								disabled={!table.getCanNextPage()}
							>
								<ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
							</Button>
							<Button
								aria-label="ไปหน้าสุดท้าย"
								variant="outline"
								className="hidden h-8 w-8 p-0 lg:flex"
								onClick={() => table.setPageIndex(table.getPageCount() - 1)}
								disabled={!table.getCanNextPage()}
							>
								<DoubleArrowRightIcon className="h-4 w-4" aria-hidden="true" />
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}
